<script setup lang="ts">
type Props = {
  variant?: 'light' | 'dark'
  type: string
  objectId: number
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'light'
})
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const { $api } = useNuxtApp()
const route = useRoute()
const isToggling = ref(false)
const { user, isAuthenticated } = useAuthUser()
const userStore = useUserStore()
const isBookmarked = computed(() =>
  userStore.isBookmarked(props.type, props.objectId)
)
const showLoginHint = ref(false)
const {getLocaleUrl} = useWadUrls();
const bookmarkWrapperRef = ref<HTMLElement | null>(null)

const closeHintOutside = (event: Event) => {
  if (showLoginHint.value && bookmarkWrapperRef.value && !bookmarkWrapperRef.value.contains(event.target as Node)) {
    showLoginHint.value = false
  }
}

onMounted(() => {
  document.addEventListener('pointerup', closeHintOutside, { capture: true })
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerup', closeHintOutside, { capture: true })
})

const toggle = async () => {
  if (!isAuthenticated.value) {
    showLoginHint.value = !showLoginHint.value
    return
  }
  showLoginHint.value = false

  if (isToggling.value) return
  isToggling.value = true

  const next = !isBookmarked.value
  userStore.toggleBookmark(props.type, props.objectId, next)

  try {
    const method = next ? 'POST' : 'DELETE'
    await $api(`/v2/user/bookmarks/${props.type}/${props.objectId}`, { method })
  } catch (err) {
    console.error('Failed to bookmark', err)
    userStore.toggleBookmark(props.type, props.objectId, !next)
  } finally {
    isToggling.value = false
  }
}

const base = 'wad4-bookmark'

const variantClass = computed(() => {
  switch (props.variant) {
    case 'transparent':
      return `${base}--transparent`
    case 'light':
      return `${base}--neutral`
    default:
      return `${base}--on-background`
  }
})

const stateClass = computed(() =>
    isBookmarked.value ? `${variantClass.value}-bookmarked` : ''
)
</script>

<template>
  <div class="wad4-bookmark-wrapper" ref="bookmarkWrapperRef">
    <button
        class="wad4-bookmark"
        :class="[variantClass, isBookmarked ? stateClass : '']"
        @pointerup.stop="toggle"
        :aria-pressed="isBookmarked"
    >
      <svg class="wad4-bookmark__icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path v-if="isBookmarked" fill-rule="evenodd" clip-rule="evenodd" d="M5.26667 2.14749C8.41166 1.78238 11.5883 1.78238 14.7333 2.14749C15.9808 2.29249 16.875 3.36416 16.875 4.58916V17.5C16.8749 17.6064 16.8475 17.7111 16.7956 17.804C16.7437 17.8969 16.6689 17.975 16.5784 18.0309C16.4878 18.0868 16.3845 18.1187 16.2782 18.1235C16.1718 18.1283 16.0661 18.1059 15.9708 18.0583L10 15.0742L4.02917 18.06C3.93382 18.1076 3.82788 18.13 3.72142 18.1251C3.61496 18.1203 3.51151 18.0883 3.42091 18.0321C3.33031 17.976 3.25556 17.8977 3.20377 17.8045C3.15198 17.7114 3.12486 17.6066 3.125 17.5V4.58916C3.125 3.36416 4.01917 2.29249 5.26667 2.14749Z" fill="currentColor"/>
        <path v-else d="M15.625 4.5894C15.625 3.96781 15.1746 3.45728 14.5882 3.38905V3.38823C11.5395 3.03439 8.4597 3.03516 5.41097 3.38905C4.82418 3.45745 4.375 3.9677 4.375 4.5894V16.4888L9.72087 13.8163L9.7876 13.787C9.9475 13.7292 10.1252 13.7394 10.2791 13.8163L15.625 16.4888V4.5894ZM16.875 17.5004C16.8749 17.7168 16.7629 17.918 16.5788 18.0318C16.3946 18.1456 16.1646 18.1554 15.9709 18.0586L10 15.0728L4.02913 18.0586C3.83542 18.1554 3.60544 18.1456 3.42122 18.0318C3.23712 17.918 3.12512 17.7168 3.125 17.5004V4.5894C3.125 3.36458 4.0189 2.29229 5.26693 2.14718C8.21515 1.80496 11.1914 1.78425 14.1431 2.08371L14.7331 2.14718L14.9626 2.18543C16.0883 2.42717 16.875 3.44088 16.875 4.5894V17.5004Z" fill="currentColor"/>
      </svg>
    </button>
    <div v-if="showLoginHint" class="wad4-bookmark__hint" @pointerup.stop @pointerdown.stop @click.stop>
      <NuxtLink class="wad4-bookmark__hint-link"
          :to="getLocaleUrl({ name: 'user-login', query: { redirect: route.fullPath } })"
          @pointerup.stop @click.stop>Login</NuxtLink>
      <span>&nbsp;to bookmark or&nbsp;</span>
      <NuxtLink class="wad4-bookmark__hint-link" :to="getLocaleUrl({ name: 'developer-sign-up', query: { redirect: route.fullPath } })"
          @pointerup.stop @click.stop>Signup</NuxtLink>
      <span>&nbsp;for free.</span>
    </div>
  </div>
</template>

<style lang="scss">
.wad4-bookmark {
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;
  border-radius: $wad4-border-radius-xs;
  display: inline-flex;
  padding: $wad4-spacing-xxs;
  justify-content: center;
  align-items: center;

  &-wrapper {
    position: relative;
    display: inline-flex;
    margin-top: $wad4-spacing-md;
  }

  &__icon {
    display: block;
    transition: fill 0.2s ease, stroke 0.2s ease;
  }

  &--neutral {
    color: $wad4-c-foreground-fg-tertiary;
    background: $wad4-c-background-bg-primary;
    &:hover {
      background: $wad4-c-background-bg-subtle;
    }

    &-bookmarked {
      color: $wad4-c-foreground-fg-brand-solid;
    }
  }

  &--on-background {
    color: $wad4-c-foreground-fg-white;
    background: $wad4-c-background-bg-black;

    &:hover {
      background: $wad4-c-background-bg-black-subtle;
    }

    &-bookmarked {
      color: $wad4-c-foreground-fg-white;
    }
  }

  &--transparent {
    background: rgba(0, 0, 0, 0.35);
    color: white;
    opacity: 0.6;
    padding: $wad4-spacing-lg $wad4-spacing-md;

    &:hover {
      opacity: 1;
    }

    &-bookmarked {
      opacity: 1;
    }
  }
  &__hint {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: $wad4-spacing-xs;
    background: $wad4-c-background-bg-brand-primary;
    padding: 9px;
    border-radius: 6px;
    width: max-content;
    max-width: calc(100cqw - 48px);
    color: $wad4-c-text-text-primary;
    z-index: 50;
    @include text-size-mixin('md');

    &-link {
      color: $wad4-c-text-text-link;
      text-decoration: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: none;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;
    }
  }
}
</style>
